/**
 * React Hook for Unified Authorization
 * 
 * Provides a clean React hook interface for accessing all authorization data
 * Now with React Query caching for better performance
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// Type definition for authorization data (matches server-side type)
export interface AuthorizationData {
    user: any;
    tier: {
        current: number;
        effective: number;
        name: string;
        color: string;
        isAdmin: boolean;
    };
    subscription: {
        status: string;
        active: boolean;
        startsAt?: string;
        expiresAt?: string;
        daysRemaining?: number;
    };
    trial: {
        isActive: boolean;
        daysRemaining: number;
        tier?: number;
        expiresAt?: string;
    };
    allTrials?: any[];
    activePromos?: any[];
    isAuthenticated: boolean;
}

export interface UseAuthResult {
    authData: AuthorizationData | null;
    user: AuthorizationData['user'];
    tier: AuthorizationData['tier'];
    subscription: AuthorizationData['subscription'];
    trial: AuthorizationData['trial'];
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;

    // Action functions
    refresh: () => Promise<void>;
    invalidateAuth: () => void;
    hasAccess: (requiredTier: number) => boolean;
    evaluateAccess: (appSlug: string, minTier?: number) => import('@/lib/authorization/types').AppAccessStatus | null;
    isAdmin: boolean;
    getAppAccess: (appSlug: string, requiredLevel: number) => { hasAccess: boolean; reason: 'subscription' | 'trial' | 'admin' | 'promo' | 'none' };
    getAppTrial: (appSlug: string, requiredLevel?: number) => { status: 'active' | 'expired' | 'never_used' | 'none'; daysRemaining?: number; canStartTrial: boolean; buttonText: string; buttonAction: 'open' | 'trial' | 'upgrade' };
    activePromos: any[];
}

/**
 * Main hook for accessing authorization data
 * Now with React Query caching (5-minute TTL)
 */
export function useAuth(): UseAuthResult {
    const queryClient = useQueryClient();

    // Use React Query for caching
    const { data: authData, isLoading, error, refetch } = useQuery({
        queryKey: ['auth'],
        queryFn: async () => {
            const response = await fetch('/api/auth/authorization', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            if (!response.ok) {
                if (response.status === 401) {
                    return null; // Not authenticated
                }
                throw new Error(`Failed to fetch authorization data: ${response.statusText}`);
            }

            return await response.json() as AuthorizationData;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes - balance between freshness and performance
        gcTime: 10 * 60 * 1000, // 10 minutes cache retention
        retry: false, // Don't retry on 401
        refetchInterval: 5 * 60 * 1000, // active poll so expired license closes standalone popups even if window never regains focus
        refetchIntervalInBackground: true,
    });

    // Refresh function (force refetch)
    const refresh = useCallback(async () => {
        await refetch();
    }, [refetch]);

    // Invalidate cache helper (for mutations)
    const invalidateAuth = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['auth'] });
    }, [queryClient]);

    // Unified access evaluation
    const evaluateAccess = useCallback((appSlug: string, minTier?: number) => {
        if (!authData || !authData.user) return null;
        const { evaluateAppAccess } = require('@/lib/authorization/permissions');
        return evaluateAppAccess(
            { slug: appSlug, min_tier: minTier },
            authData.user,
            authData.allTrials || [],
            authData.activePromos || [],
        );
    }, [authData]);

    const hasAccess = useCallback((requiredTier: number) => {
        // Level 0 is ALWAYS accessible - no checks required
        if (requiredTier === 0) {
            return true;
        }
        return authData ? authData.tier.effective >= requiredTier : false;
    }, [authData]);

    // Get app-specific access information (from auth system)
    const getAppAccess = useCallback((appSlug: string, requiredLevel: number) => {
        // Level 0 apps are ALWAYS accessible - no checks required
        if (requiredLevel === 0) {
            return { hasAccess: true, reason: 'subscription' as const };
        }

        if (!authData?.isAuthenticated) {
            return { hasAccess: false, reason: 'none' as const };
        }

        // Admin has access to everything
        if (authData.tier.isAdmin) {
            return { hasAccess: true, reason: 'admin' as const };
        }

        // Check real subscription (requires an actual active subscription, not trial-elevated tier)
        const hasRealSubscription = authData.subscription?.active && !!authData.subscription?.expiresAt;
        if (hasRealSubscription && authData.tier.effective >= requiredLevel) {
            return { hasAccess: true, reason: 'subscription' as const };
        }

        // Check active promotion — grants access regardless of subscription
        const promo = authData.activePromos?.find(
            (p: any) => p.app_slug === appSlug
        );
        if (promo) {
            return { hasAccess: true, reason: 'promo' as const };
        }

        // Check for active trial — level-based (new) or per-app slug (legacy)
        const trial = authData.allTrials?.find(
            (t: any) => t.status === 'active' && (
                t.level === requiredLevel ||
                t.app_slug === appSlug
            )
        );

        if (trial) {
            return { hasAccess: true, reason: 'trial' as const };
        }

        return { hasAccess: false, reason: 'none' as const };
    }, [authData]);

    // Get trial status for an app, resolved by level (new) or app_slug (legacy)
    const getAppTrial = useCallback((appSlug: string, requiredLevel?: number) => {
        const levelLabel = requiredLevel !== undefined ? `Level ${requiredLevel}` : null;

        // 1. Look for active level trial first
        let trial: any = undefined;
        if (requiredLevel !== undefined) {
            trial = authData?.allTrials?.find(
                (t: any) => t.level === requiredLevel && t.status === 'active'
            ) ?? authData?.allTrials?.find(
                (t: any) => t.level === requiredLevel
            );
        }

        // 2. Fall back to legacy per-app slug lookup
        if (!trial) {
            trial = authData?.allTrials?.find((t: any) => t.app_slug === appSlug);
        }

        if (!trial) {
            return {
                status: 'none' as const,
                canStartTrial: true,
                buttonText: levelLabel ? `Start 5-Day Trial for ${levelLabel}` : 'Start 5-Day Trial',
                buttonAction: 'trial' as const,
            };
        }

        if (trial.status === 'active') {
            return {
                status: 'active' as const,
                daysRemaining: trial.days_remaining,
                canStartTrial: false,
                buttonText: levelLabel
                    ? `${levelLabel} Trial Active — ${trial.days_remaining} days left`
                    : `Trial Active — ${trial.days_remaining} days left`,
                buttonAction: 'open' as const,
            };
        }

        if (trial.status === 'expired') {
            return {
                status: 'expired' as const,
                daysRemaining: 0,
                canStartTrial: false,
                buttonText: levelLabel
                    ? `${levelLabel} Trial Expired — Upgrade`
                    : 'Trial Expired — Upgrade',
                buttonAction: 'upgrade' as const,
            };
        }

        return {
            status: 'never_used' as const,
            canStartTrial: true,
            buttonText: levelLabel ? `Start 5-Day Trial for ${levelLabel}` : 'Start 5-Day Trial',
            buttonAction: 'trial' as const,
        };
    }, [authData]);

    return {
        authData: authData || null,
        user: authData?.user || null,
        tier: authData?.tier || { current: 0, effective: 0, name: 'Free', color: 'text-gray-500', isAdmin: false },
        subscription: authData?.subscription || { status: 'inactive', active: false },
        trial: authData?.trial || { isActive: false, daysRemaining: 0 },
        isAuthenticated: authData?.isAuthenticated || false,
        isLoading,
        error: error ? (error as Error).message : null,
        refresh,
        invalidateAuth,
        hasAccess,
        evaluateAccess,
        isAdmin: authData?.tier.isAdmin || false,
        activePromos: authData?.activePromos || [],
        // App-specific helpers (from auth system)
        getAppAccess,
        getAppTrial
    };
}

/**
 * Hook for components that only need user data
 */
export function useUser() {
    const { user, isLoading, error } = useAuth();
    return { user, isLoading, error };
}

/**
 * Hook for components that only need tier information
 */
export function useTier() {
    const { tier, isLoading, error } = useAuth();
    return { tier, isLoading, error };
}

/**
 * Hook for components that only need trial information
 */
export function useTrial() {
    const { trial, isLoading, error } = useAuth();
    return { trial, isLoading, error };
}

/**
 * Hook for access control
 */
export function useAccess() {
    const { hasAccess, isAdmin, tier, isLoading } = useAuth();

    const checkAccess = useCallback((requiredTier: number) => {
        if (isLoading) return false; // Deny access while loading
        return hasAccess(requiredTier);
    }, [hasAccess, isLoading]);

    return {
        hasAccess: checkAccess,
        isAdmin,
        tier,
        isLoading
    };
}